import type { MascotEmotion } from "./emotions";

/**
 * Warstwa 3 — konteksty (zdarzenia w apce). Każdy kontekst mapuje się na
 * preset: emocja + ton koloru + intensywność.
 *
 * Używany przez `<MascotMoment context="level-up" data={…}/>` — komponent
 * sam wybierze emocję, ton i layout z presetu.
 */
export type MascotContext =
  | "task-submitted"   // po addCompletion (auto-dismiss 3s)
  | "task-approved"    // po approveCompletion (push + opcjonalny moment)
  | "level-up"         // level wzrósł vs lastSeenLevel
  | "badge-unlocked"   // nowa odznaka
  | "streak-milestone" // 3/7/30/100 dni
  | "streak-broken"    // streak zerwany, dziecko wraca
  | "shield-used"      // tarcza zużyta
  | "goal-reached"     // saldo ≥ goal.target po raz pierwszy
  | "first-payout"     // pierwsza wypłata
  | "empty-state"      // brak zadań na dziś
  | "comeback"         // login po >5 dniach przerwy
  | "welcome"          // pierwszy login
  | "complete-day"     // wszystkie zadania dnia approved
  | "parent-philosophy"; // manifest marki dla rodzica (raz)

export type MascotTone = "hay" | "purple" | "green" | "gray";
export type MascotIntensity = "ambient" | "small" | "big";

export type MascotPreset = {
  emotion: MascotEmotion;
  tone: MascotTone;
  intensity: MascotIntensity;
};

export const CONTEXT_PRESETS: Record<MascotContext, MascotPreset> = {
  "task-submitted":   { emotion: "celebrate",  tone: "hay",    intensity: "small" },
  "task-approved":    { emotion: "happy",      tone: "green",  intensity: "small" },
  "level-up":         { emotion: "proud",      tone: "purple", intensity: "big"   },
  "badge-unlocked":   { emotion: "surprised",  tone: "purple", intensity: "small" },
  "streak-milestone": { emotion: "celebrate",  tone: "hay",    intensity: "big"   },
  "streak-broken":    { emotion: "sad",        tone: "gray",   intensity: "small" },
  "shield-used":      { emotion: "protective", tone: "green",  intensity: "small" },
  "goal-reached":     { emotion: "celebrate",  tone: "purple", intensity: "big"   },
  "first-payout":     { emotion: "proud",      tone: "hay",    intensity: "big"   },
  "empty-state":      { emotion: "sleepy",     tone: "hay",    intensity: "ambient" },
  "comeback":         { emotion: "happy",      tone: "green",  intensity: "small" },
  "welcome":          { emotion: "happy",      tone: "green",  intensity: "big"   },
  "complete-day":     { emotion: "proud",      tone: "hay",    intensity: "small" },
  "parent-philosophy":{ emotion: "happy",      tone: "hay",    intensity: "big"   },
};

/**
 * Background gradient per ton — używany przez `<MascotMoment>` jako tło
 * fullscreen overlay. Zwraca CSS `background` value.
 */
export function toneBackground(tone: MascotTone): string {
  switch (tone) {
    case "hay":
      return "var(--kd-hay)";
    case "purple":
      return "linear-gradient(135deg, #8E7DF2 0%, #7565E8 100%)";
    case "green":
      return "linear-gradient(135deg, var(--kd-green) 0%, var(--kd-forest) 100%)";
    case "gray":
      return "var(--kd-cloud)";
  }
}

/**
 * Kolor tekstu (white na ciemnych tonach, navy na jasnych).
 */
export function toneTextColor(tone: MascotTone): "white" | "navy" {
  return tone === "purple" || tone === "green" ? "white" : "navy";
}
